<template>
  <carousel-component
    v-if="renderedSlides.length"
    :min-item-width="300"
    :item-data="renderedSlides"
    :displayed-quantity="1"
    :autoscroll-delay="10000"
    :scrolled-quantity="1"
    :max-per-line="renderedSlides.length"
    :show-arrow="false"
    class="main-banner"
    need-infinity
    show-dots
  >
    <template v-slot:default="{ props: slide }">
      <div class="main-banner__item"
           :class="[slide.content_position || 'center-left']"
           :style="{
           '--text-color': slide.text_color,
           '--background': bannerBackground(slide.opacity, slide.background_color),
           textAlign: slide.text_align,
        }">
        <video
          v-if="slide.video"
          class="main-banner__image"
          :src="getImagePath(slide.video)"
          playsinline
          autoplay
          loop
          muted
        />
        <image-component
          v-else
          class="main-banner__image"
          :path="slide.image"
        />
        <div
          v-if="slide.main_title || slide.second_title || slide.btn_title"
          class="main-banner__content"
        >
          <h2 v-if="slide.main_title" class="main-banner__title">{{ slide.main_title }}</h2>
          <h4 v-if="slide.second_title" class="main-banner__subtitle">{{ slide.second_title }}</h4>
          <app-link
            v-if="slide.btn_title"
            :to="slide.link"
            class="main-banner__button"
            :style="{
                'background-color': slide.btn_color  || 'transparent',
                '--text': slide.btn_text_color
              }"
          >
            {{ slide.btn_title }}
          </app-link>
        </div>
      </div>
    </template>
  </carousel-component>
</template>

<script>
import ImageComponent from '../../../components/base-components/ImageComponent';
import {mapGetters, mapState} from 'vuex';
import {SLIDES_TYPES, URL_QUERIES} from '@/const';
import {GET_CHANNEL_SLIDES_FOR_QUERY} from '@/store/getterTypes';
import {mapCacheActions} from 'vuex-cache';
import {GET_CHANNEL_SLIDES} from '@/store/actionTypes';
import CarouselComponent from '@/components/base-components/CarouselComponent.vue';
import AppLink from '@/components/base-components/AppLink.vue';
import {getHexAlpha} from '@/utils/colorScheme';

export default {
  name: 'SliderComponent',
  components: {
    AppLink,
    CarouselComponent,
    ImageComponent,
  },
  data() {
    return {
      categoryQuery: this.$route.query?.[URL_QUERIES.category]?.toLowerCase(),
      participantGroupQuery: this.$route.query?.[URL_QUERIES.participantGroup]?.toLowerCase(),
      participantQuery: this.$route.query?.[URL_QUERIES.PARTICIPANT]?.toLowerCase(),
    };
  },
  computed: {
    ...mapState({
      slides: (state) => state.channel.slides,
    }),
    ...mapGetters({
      slidesGetter: GET_CHANNEL_SLIDES_FOR_QUERY,
    }),
    renderedSlides() {
      let selectedSlides;
      if (this.categoryQuery) {
        selectedSlides = this.slidesGetter(SLIDES_TYPES.category, this.categoryQuery);
      }
      if (this.participantGroupQuery) {
        selectedSlides = this.slidesGetter(SLIDES_TYPES.group, this.participantGroupQuery);
      }
      if (this.participantQuery) {
        selectedSlides = this.slidesGetter(SLIDES_TYPES.participant, this.participantQuery);
      }
      return selectedSlides || this.slides[SLIDES_TYPES.main];
    },
  },
  methods: {
    ...mapCacheActions({
      getChannelSlides: GET_CHANNEL_SLIDES,
    }),
    bannerBackground(alpha, color) {
      return `${color}${getHexAlpha(alpha * 100)}`;
    },
  },
  watch: {
    $route(to) {
      this.categoryQuery = to.query?.[URL_QUERIES.category]?.toLowerCase?.();
      this.participantGroupQuery = to.query?.[URL_QUERIES.participantGroup]?.toLowerCase?.();
      this.participantQuery = to.query?.[URL_QUERIES.PARTICIPANT]?.toLowerCase?.();
    },
  },
  mounted() {
    !this.slides.requested && this.getChannelSlides();
  },
};
</script>

<style lang="scss">
@import "src/assets/css/colors";
@import "src/assets/css/mixins";

.main-banner {
  margin-top: 4.375rem;
  max-width: 100vw;
}

.main-banner__item {
  max-width: 100vw;
  position: relative;
  background: var(--background);
  color: var(--text-color);
  font-style: normal;
  text-align: left;
  overflow: visible;
}

.main-banner__image {
  object-fit: fill;
  width: 100vw;
  float: right;
}

.main-banner__content {
  position: absolute;
  width: 45%;
  max-height: 100%;
  overflow-y: auto;
  padding: 1rem 10% 1rem 2rem;
  clip-path: polygon(0 0, 90% 0, 100% 100%, 0 100%);
  backdrop-filter: blur(.15rem);
  display: flex;
  flex-direction: column;
  gap: .5rem;
  justify-content: center;
}


.main-banner__title {
  font-weight: 700;
  font-size: clamp(.9rem, 2.5vw, 2.125rem)
}

.main-banner__subtitle {
  font-weight: 400;
  font-size: clamp(.8rem, 2vw, 1.25rem)
}

.main-banner__button {
  background: transparent;
  display: inline-block;
  padding: .75rem 1.25rem;
  color: var(--text);
  transition: all .2s linear;
  position: relative;
  align-self: center;

  &:before {
    width: 10%;
    height: 25%;
    border-top: 1px solid var(--text);
    border-left: 1px solid var(--text);
    position: absolute;
    content: '';
    display: block;
    top: 0;
    left: 0;
    transition: all .15s linear;
  };

  &:after {
    position: absolute;
    content: '';
    display: block;
    width: 10%;
    height: 25%;
    border-bottom: 1px solid var(--text);
    border-right: 1px solid var(--text);
    bottom: 0;
    right: 0;
    transition: all .15s linear;
  }

  &:hover {
    text-decoration: none;
    color: var(--text);

    &:before,
    &:after {
      width: 100%;
      height: 100%;
    }
  }
}
.top-left{
  .main-banner__content{
    top: 0;
    left: 0;
    transform: none;
    background: linear-gradient(65deg, var(--background) 80%, transparent 90%);
  }
}
.center-left{
  .main-banner__content{
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    background: linear-gradient(65deg, var(--background) 80%, transparent 90%);
  }
}
.bottom-left{
  .main-banner__content{
    top: initial;
    bottom: 0;
    left: 0;
    transform: none;
    background: linear-gradient(65deg, var(--background) 80%, transparent 90%);
  }
}
.top-right{
  .main-banner__content{
    top: 0;
    right: 0;
    background: linear-gradient(290deg, var(--background) 80%,  transparent 90%);
    clip-path: polygon(10% 0, 100% 0, 100% 100%, 0 100%);
    padding: 1rem 2rem 1rem 10%;
  }
}
.center-right{
  .main-banner__content{
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    background: linear-gradient(290deg, var(--background) 80%,  transparent 90%);
    clip-path: polygon(10% 0, 100% 0, 100% 100%, 0 100%);
    padding: 1rem 2rem 1rem 10%;
  }
}
.bottom-right{
  .main-banner__content{
    bottom: 0;
    right: 0;
    background: linear-gradient(290deg, var(--background) 80%,  transparent 90%);
    clip-path: polygon(10% 0, 100% 0, 100% 100%, 0 100%);
    padding: 1rem 2rem 1rem 10%;
  }
}
.center{
  .main-banner__content{
    top: 50%;
    transform: translateY(-50%) translateX(-50%);
    left: 50%;
    background: linear-gradient(105deg, transparent 10%, var(--background) 20%,
      var(--background) 80%,  transparent 90%);
    clip-path: polygon(10% 0, 100% 0, 90% 100%, 0 100%);
    padding: 1rem 10% 1rem 10%;
  }
}

@include media-max(md) {
  .main-banner{
    margin-top: 3rem;
  }
  .main-banner__button {
    padding: .25rem .5rem;
    font-weight: 500;
    font-size: .8rem;
    align-self: flex-start;
  }
}
@include media-max(xs) {
  .main-banner__item{
    display: flex;
    flex-direction: column;
  }
  .main-banner__image {
    mask-image: linear-gradient(to bottom, black calc(100% - 2rem), transparent);
  }
  .main-banner__item{
    .main-banner__content{
      position: static;
      transform: none;
      width: 100%;
      background: transparent;
      flex-grow: 1;
      padding: 1rem;
      clip-path: none;
    }
  }
}
</style>
